import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Phone, Mail, MapPin, Clock, ChevronDown, ChevronRight,
  Wrench, Disc3, Cog, Battery, Zap, Filter, CircleDot, Gauge, Search,
  ShieldCheck, Sparkles, Award, Truck, CheckCircle2, Menu, X, ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// API base kept for future use; quote form replaced by direct WhatsApp CTA.
// eslint-disable-next-line no-unused-vars
const API = `${BACKEND_URL}/api`;

const PHONE_MAIN = "+33656660640";
const PHONE_DISPLAY = "+33 6 56 66 06 40";
const WHATSAPP = "33656660640";
const EMAIL = "abpa74100@gmail.com";

const BRANDS = [
  { name: "Bosch", logo: "https://logo.clearbit.com/bosch.com" },
  { name: "Brembo", logo: "https://logo.clearbit.com/brembo.com" },
  { name: "Valeo", logo: "https://logo.clearbit.com/valeo.com" },
  { name: "SKF", logo: "https://logo.clearbit.com/skf.com" },
  { name: "Continental", logo: "https://logo.clearbit.com/continental.com" },
  { name: "NGK", logo: "https://logo.clearbit.com/ngkntk.com" },
  { name: "Mann-Filter", logo: "https://logo.clearbit.com/mann-filter.com" },
  { name: "Gates", logo: "https://logo.clearbit.com/gates.com" },
  { name: "Schaeffler / LUK", logo: "https://logo.clearbit.com/schaeffler.com" },
  { name: "ZF Sachs", logo: "https://logo.clearbit.com/zf.com" },
  { name: "Febi", logo: "https://logo.clearbit.com/febi.com" },
  { name: "NTN-SNR", logo: "https://logo.clearbit.com/ntn-snr.com" },
];

const CATEGORIES = [
  { name: "Moteur", icon: Cog, img: "https://images.pexels.com/photos/8237050/pexels-photo-8237050.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Freinage", icon: Disc3, img: "https://images.pexels.com/photos/34277926/pexels-photo-34277926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Suspension", icon: CircleDot, img: "https://images.pexels.com/photos/11532598/pexels-photo-11532598.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Échappement", icon: Wrench, img: "https://images.pexels.com/photos/8986031/pexels-photo-8986031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Électronique", icon: Zap, img: "https://images.pexels.com/photos/12071020/pexels-photo-12071020.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Filtration", icon: Filter, img: "https://images.pexels.com/photos/8985928/pexels-photo-8985928.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { name: "Batterie", icon: Battery, img: "https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?auto=format&fit=crop&w=1200&q=80" },
  { name: "Transmission", icon: Gauge, img: "https://images.unsplash.com/photo-1632823471565-1ecdf5c6da77?auto=format&fit=crop&w=1200&q=80" },
  { name: "Pièce spécifique", icon: Search, img: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=1200&q=80", highlight: true },
];

const PILLARS = [
  { icon: Award, title: "Qualité OEM", desc: "Pièces de marques d'origine constructeur (BOSCH, BREMBO, VALEO...) — fiabilité garantie." },
  { icon: ShieldCheck, title: "Garantie pièces", desc: "Toutes nos pièces sont garanties pour votre tranquillité d'esprit." },
  { icon: Sparkles, title: "Prix compétitifs", desc: "Devis personnalisé au meilleur tarif. On compare pour vous." },
  { icon: Truck, title: "Livraison rapide", desc: "Pièces courantes livrées l'après-midi même ou le lendemain matin. Service livraison réservé aux clients professionnels." },
];

const STATS = [
  { value: 5000, suffix: "+", label: "Clients satisfaits" },
  { value: 50, suffix: "+", label: "Marques partenaires" },
  { value: 1000, suffix: "+", label: "Pièces référencées" },
  { value: 2, suffix: "", label: "Agences en Haute-Savoie" },
];

const FAQS = [
  { q: "Comment passer une commande ou demander un devis ?", a: "Remplissez notre formulaire en ligne avec marque, modèle, immatriculation et la pièce recherchée. Nous vous envoyons un devis personnalisé rapidement. Vous pouvez aussi nous appeler directement — Ville-la-Grand : +33 4 51 42 06 04 · Marnaz : +33 4 51 42 03 60. Pour un SMS ou WhatsApp : +33 6 56 66 06 40." },
  { q: "Quelles marques de pièces proposez-vous ?", a: "Nous travaillons avec les meilleures marques constructeur : LUK, SACHS, GATES, SNR, SKF, BOSCH, BREMBO, VALEO, MANN-FILTER, CONTINENTAL et bien d'autres." },
  { q: "Faites-vous la livraison ?", a: "Pour les pièces courantes (non spécifiques), livraison l'après-midi même ou le lendemain matin. La livraison est réservée à nos clients professionnels (garages, carrosseries). Les particuliers retirent en agence." },
  { q: "Vendez-vous aux particuliers et aux professionnels ?", a: "Oui, nous fournissons aussi bien les particuliers que les garages, carrosseries et mécaniciens indépendants avec des tarifs adaptés." },
  { q: "Vos pièces sont-elles garanties ?", a: "Oui, toutes nos pièces neuves sont garanties. La durée dépend de la marque et du type de pièce. Précisée sur votre devis." },
];

const HOURS = [
  { day: "Lundi", h: "9h - 12h · 14h - 18h" },
  { day: "Mardi", h: "9h - 12h · 14h - 18h" },
  { day: "Mercredi", h: "9h - 12h · 14h - 18h" },
  { day: "Jeudi", h: "9h - 12h · 14h - 18h" },
  { day: "Vendredi", h: "9h - 12h · 14h - 18h" },
  { day: "Samedi", h: "9h - 13h" },
  { day: "Dimanche", h: "Fermé", closed: true },
];

const LOCATIONS = [
  {
    id: "ville-la-grand",
    name: "Ville-la-Grand",
    address: "16 Rue Deux Montagnes au Québec, 74100 Ville-la-Grand",
    phone: "+33 4 51 42 06 04",
    email: "abpa74100@gmail.com",
    map: "https://www.google.com/maps?q=16+Rue+Deux+Montagnes+au+Quebec+74100+Ville-la-Grand&output=embed",
  },
  {
    id: "marnaz",
    name: "Marnaz",
    address: "597 avenue du Mont-Blanc, 74460 Marnaz",
    phone: "+33 4 51 42 03 60",
    email: "abap74460@gmail.com",
    map: "https://www.google.com/maps?q=597+avenue+du+Mont-Blanc+74460+Marnaz&output=embed",
  },
];

// ==================== SUBCOMPONENTS ====================

function Logo({ className = "" }) {
  return (
    <a href="#home" className={`flex items-center gap-3 group ${className}`} data-testid="logo-home">
      <img
        src="https://customer-assets.emergentagent.com/job_automotive-shop-74/artifacts/1bq4t0lo_IMG_7777.jpeg"
        alt="AB Pièces Auto Annemasse"
        className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
        loading="eager"
      />
    </a>
  );
}

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (inView) {
      const controls = animate(count, to, { duration: 2.2, ease: [0.22, 1, 0.36, 1] });
      const unsub = rounded.on("change", (v) => setDisplay(v));
      return () => { controls.stop(); unsub(); };
    }
  }, [inView, to, count, rounded]);

  return <span ref={ref}>{display}{suffix}</span>;
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#categories", label: "Catégories" },
    { href: "#pourquoi", label: "Pourquoi nous" },
    { href: "#devis", label: "Devis" },
    { href: "#agences", label: "Agences" },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <a key={l.href} href={l.href} className="text-sm tracking-widest uppercase font-semibold text-neutral-300 hover:text-[#E63027] transition-colors relative group" data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#E63027] group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <a href={`tel:${PHONE_MAIN}`} className="flex items-center gap-2 text-white hover:text-[#FCD500] transition-colors" data-testid="header-phone">
              <Phone className="w-4 h-4" />
              <span className="font-display text-xl tracking-wider">{PHONE_DISPLAY}</span>
            </a>
            <a href="#devis" className="bg-[#E63027] hover:bg-[#C81D15] text-white font-bold uppercase tracking-wider text-sm px-5 py-3 transition-all hover:-translate-y-0.5" data-testid="header-quote-cta">
              Devis gratuit
            </a>
          </div>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-black border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-5">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-base font-semibold tracking-wider uppercase text-neutral-200 hover:text-[#E63027]" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            ))}
            <div className="pt-4 border-t border-white/10 space-y-3">
              <a href={`tel:${PHONE_MAIN}`} className="flex items-center gap-2 text-[#FCD500] font-display text-2xl">
                <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
              </a>
              <a href="#devis" onClick={() => setOpen(false)} className="block bg-[#E63027] text-white text-center font-bold uppercase py-3">
                Devis gratuit
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 hero-bg"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=1920&q=85)" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1B1B1F]/95 via-[#1B1B1F]/75 to-[#1B1B1F]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B1F]/85 via-transparent to-transparent" />
      <div className="absolute top-1/2 right-0 w-1 h-64 bg-[#E63027]" />
      <div className="absolute top-1/2 right-2 w-1 h-32 bg-[#FCD500]" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 bg-[#E63027]/10 border border-[#E63027]/40 px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-[#FCD500] rounded-full animate-pulse" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#FCD500] font-bold">N°1 à Ville-la-Grand · Marnaz</span>
          </div>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.9] text-white">
            Vos pièces<br />
            auto au<br />
            <span className="text-[#E63027]">meilleur</span> <span className="text-stroke">tarif.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-neutral-300 max-w-xl leading-relaxed">
            Toutes pièces de rechange et accessoires — moteurs, freinage, suspension, électronique. Devis gratuit, marques OEM, garantie incluse. Livraison après-midi même pour les pros.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 items-stretch">
            <a href="#devis" className="group bg-[#E63027] hover:bg-[#C81D15] text-white font-bold uppercase tracking-wider px-8 py-5 flex items-center gap-3 transition-all hover:-translate-y-1 glow-red" data-testid="hero-quote-cta">
              Demander un devis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="tel:+33451420604" className="group bg-transparent hover:bg-white/5 border-2 border-white/30 hover:border-[#FCD500] text-white px-5 py-3 flex items-center gap-3 transition-all" data-testid="hero-call-villa">
                <Phone className="w-4 h-4 text-[#E63027]" />
                <span className="flex flex-col leading-tight items-start">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#FCD500] font-bold">Ville-la-Grand</span>
                  <span className="font-bold tracking-wider text-sm md:text-base">+33 4 51 42 06 04</span>
                </span>
              </a>
              <a href="tel:+33451420360" className="group bg-transparent hover:bg-white/5 border-2 border-white/30 hover:border-[#FCD500] text-white px-5 py-3 flex items-center gap-3 transition-all" data-testid="hero-call-marnaz">
                <Phone className="w-4 h-4 text-[#E63027]" />
                <span className="flex flex-col leading-tight items-start">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#FCD500] font-bold">Marnaz</span>
                  <span className="font-bold tracking-wider text-sm md:text-base">+33 4 51 42 03 60</span>
                </span>
              </a>
            </div>
          </div>
          <div className="mt-14 flex flex-wrap gap-8 items-center">
            {[
              { icon: ShieldCheck, t: "Garantie pièces" },
              { icon: Award, t: "Marques OEM" },
              { icon: Truck, t: "Livraison pros" },
            ].map(({ icon: Icon, t }, i) => (
              <div key={i} className="flex items-center gap-2 text-neutral-400">
                <Icon className="w-5 h-5 text-[#FCD500]" />
                <span className="text-sm tracking-wider uppercase font-semibold">{t}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-neutral-500"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}

function BrandsMarquee() {
  return (
    <section className="relative py-14 bg-[#1B1B1F] border-y border-white/5 overflow-hidden" data-testid="brands-marquee">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <p className="text-xs tracking-[0.4em] uppercase text-neutral-400 font-bold">Marques constructeur partenaires</p>
      </div>
      <div className="marquee-fade">
        <Marquee speed={45} gradient={false} pauseOnHover>
          {BRANDS.map((b, i) => (
            <div
              key={i}
              className="mx-4 md:mx-5 h-24 w-44 md:w-52 bg-white flex items-center justify-center px-6 grayscale hover:grayscale-0 transition-all duration-300 hover:scale-105"
              title={b.name}
            >
              <img
                src={b.logo}
                alt={b.name}
                loading="lazy"
                className="max-h-12 max-w-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="font-display text-2xl text-neutral-800 tracking-wider">${b.name}</span>`;
                }}
              />
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function Categories() {
  return (
    <section id="categories" className="relative py-24 md:py-32 bg-[#1B1B1F]" data-testid="categories-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6"
        >
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-[#E63027] font-bold mb-3">— Notre catalogue</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
              Toutes les <span className="text-[#FCD500]">pièces</span><br />de rechange.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md text-lg">
            Du moteur à la suspension, en passant par le freinage et l'électronique — nous fournissons tout ce dont votre véhicule a besoin.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <motion.a
                key={cat.name}
                href="#devis"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group relative aspect-[4/5] overflow-hidden bg-[#25252B] border transition-all ${cat.highlight ? 'border-[#FCD500] hover:border-[#FCD500]' : 'border-[#3A3A42] hover:border-[#E63027]/60'}`}
                data-testid={`category-${cat.name.toLowerCase().replace(/\s/g, '-').replace(/[éè]/g, 'e')}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                  style={{ backgroundImage: `url(${cat.img})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10" />
                {cat.highlight && (
                  <div className="absolute top-4 right-4 bg-[#FCD500] text-black text-[10px] font-bold uppercase tracking-widest px-2 py-1 z-10">
                    Sur demande
                  </div>
                )}
                <div className="relative h-full flex flex-col justify-between p-5 md:p-6">
                  <div className={`w-12 h-12 flex items-center justify-center group-hover:rotate-12 transition-transform duration-500 ${cat.highlight ? 'bg-[#FCD500] text-black' : 'bg-[#E63027] text-white'}`}>
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl text-white tracking-wide">{cat.name}</h3>
                    <div className={`mt-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${cat.highlight ? 'text-[#FCD500]' : 'text-[#FCD500]'}`}>
                      {cat.highlight ? 'Demander' : 'Voir'} <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section id="pourquoi" className="relative py-24 md:py-32 bg-[#1B1B1F] overflow-hidden" data-testid="why-section">
      <div className="absolute top-0 left-0 w-1/3 h-full stripes opacity-30" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 lg:sticky lg:top-32"
          >
            <p className="text-xs tracking-[0.4em] uppercase text-[#E63027] font-bold mb-3">— Pourquoi nous choisir</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
              L'expertise<br /> qui fait la<br /><span className="text-[#E63027]">différence.</span>
            </h2>
            <p className="mt-6 text-neutral-400 text-lg max-w-md">
              Spécialiste local des pièces auto en Haute-Savoie. Nous travaillons avec les meilleures marques constructeur pour fournir des pièces de qualité supérieure à des prix vraiment compétitifs.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-[#E63027] pl-4">
                  <div className="font-display text-4xl md:text-5xl text-white">
                    <Counter to={s.value} suffix={s.suffix} />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-neutral-500 mt-1 font-bold">{s.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 md:gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="card-sharp p-7 md:p-8 hover-lift"
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="w-14 h-14 bg-[#E63027]/10 border border-[#E63027]/30 flex items-center justify-center mb-5 group-hover:bg-[#E63027] transition-colors">
                    <Icon className="w-7 h-7 text-[#E63027]" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-2xl text-white tracking-wide mb-3">{p.title}</h3>
                  <p className="text-neutral-400 leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function QuoteForm() {
  const waText = encodeURIComponent(
    "Bonjour AB Pièces Auto Annemasse,\n\nJe souhaite un devis pour une pièce auto.\n\nMarque : \nModèle : \nImmatriculation ou VIN : \nPièce(s) recherchée(s) : \nAgence préférée : Ville-la-Grand / Marnaz\n\nMerci !"
  );
  const waLink = `https://wa.me/${WHATSAPP}?text=${waText}`;

  return (
    <section id="devis" className="relative py-24 md:py-32 bg-[#1B1B1F] overflow-hidden" data-testid="quote-section">
      <div className="absolute top-0 inset-x-0 angle-divider" />
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#25D366]/10 blur-3xl rounded-full" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#E63027]/10 blur-3xl rounded-full" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center"
        >
          <p className="text-xs tracking-[0.4em] uppercase text-[#E63027] font-bold mb-4">— Devis gratuit & rapide</p>
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
            Envoyez-nous<br />un message<br /><span className="text-[#25D366]">sur WhatsApp</span>.
          </h2>
          <p className="mt-8 text-neutral-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            Pour un devis rapide, indiquez-nous simplement :
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left">
            {[
              "Marque du véhicule",
              "Modèle du véhicule",
              "Immatriculation ou VIN",
              "Pièce(s) recherchée(s)",
            ].map((it, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border border-[#3A3A42] bg-[#25252B]">
                <CheckCircle2 className="w-5 h-5 text-[#FCD500] flex-shrink-0" />
                <span className="text-sm md:text-base font-semibold text-white">{it}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center gap-6">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-4 bg-[#25D366] hover:bg-[#1faf54] text-white font-bold uppercase tracking-wider text-base md:text-lg px-10 md:px-14 py-6 md:py-7 transition-all hover:-translate-y-1 shadow-[0_10px_40px_rgba(37,211,102,0.35)] hover:shadow-[0_15px_50px_rgba(37,211,102,0.5)]"
              data-testid="whatsapp-cta-main"
            >
              <svg viewBox="0 0 32 32" className="w-7 h-7 md:w-8 md:h-8" fill="white" aria-hidden="true">
                <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.916 2.722.916.817 0 2.15-.516 2.495-1.318.187-.43.187-.802.115-1.246-.36-.244-.802-.488-1.232-.703-.302-.143-.602-.302-.86-.5-.072-.058-.14-.116-.244-.116m-3.025 8.27c-1.95 0-3.84-.516-5.485-1.535l-.387-.243-4.013 1.05 1.07-3.93-.258-.41a10.844 10.844 0 0 1-1.66-5.762c0-5.99 4.882-10.87 10.87-10.87 5.99 0 10.872 4.882 10.872 10.87 0 5.99-4.883 10.83-10.87 10.83m9.243-20.07a13.082 13.082 0 0 0-9.242-3.825C8.794 1.58 2.86 7.514 2.86 14.844c0 2.336.616 4.626 1.79 6.65L2.745 28.42l7.075-1.85a13.05 13.05 0 0 0 6.235 1.59h.014c7.276 0 13.21-5.92 13.21-13.27 0-3.522-1.39-6.836-3.882-9.328"/>
              </svg>
              <span>Écrire sur WhatsApp</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 font-bold">
              {PHONE_DISPLAY} · Réponse rapide
            </p>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto">
            {[
              { t: "Réponse rapide" },
              { t: "Marques OEM" },
              { t: "Tarif compétitif" },
              { t: "Sans engagement" },
            ].map((it, i) => (
              <div key={i} className="card-sharp p-5 flex items-center gap-3 justify-center">
                <CheckCircle2 className="w-5 h-5 text-[#E63027] flex-shrink-0" />
                <span className="text-sm font-semibold text-neutral-200">{it.t}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center gap-4 px-6 py-6 border border-[#3A3A42] bg-[#25252B] max-w-2xl mx-auto">
            <span className="text-xs tracking-widest uppercase text-neutral-400 font-bold">Préférez un appel ?</span>
            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
              <a href="tel:+33451420604" className="flex flex-col items-center gap-1 group" data-testid="quote-phone-villa">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#FCD500] font-bold">Ville-la-Grand</span>
                <span className="font-display text-2xl md:text-3xl text-white group-hover:text-[#FCD500] transition-colors flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#E63027]" /> +33 4 51 42 06 04
                </span>
              </a>
              <div className="hidden sm:block w-px h-12 bg-[#3A3A42]" />
              <a href="tel:+33451420360" className="flex flex-col items-center gap-1 group" data-testid="quote-phone-marnaz">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#FCD500] font-bold">Marnaz</span>
                <span className="font-display text-2xl md:text-3xl text-white group-hover:text-[#FCD500] transition-colors flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#E63027]" /> +33 4 51 42 03 60
                </span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Locations() {
  return (
    <section id="agences" className="relative py-24 md:py-32 bg-[#141418]" data-testid="locations-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <p className="text-xs tracking-[0.4em] uppercase text-[#E63027] font-bold mb-3">— Nos agences</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
            2 adresses<br />en <span className="text-[#FCD500]">Haute-Savoie</span>.
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {LOCATIONS.map((loc, i) => (
            <motion.div
              key={loc.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="card-sharp p-0 overflow-hidden"
              data-testid={`location-${loc.id}`}
            >
              <div className="aspect-[16/10] bg-[#1B1B1F] relative overflow-hidden">
                <iframe
                  title={loc.name}
                  src={loc.map}
                  className="w-full h-full grayscale-[40%] contrast-110"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="p-6 md:p-8 space-y-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-[#FCD500] font-bold mb-1">Agence</p>
                    <h3 className="font-display text-4xl text-white tracking-wide">{loc.name}</h3>
                  </div>
                  <div className="w-12 h-12 bg-[#E63027] flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-neutral-300">
                    <MapPin className="w-4 h-4 text-[#E63027] mt-1 flex-shrink-0" />
                    <span className="text-sm">{loc.address}</span>
                  </div>
                  <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-neutral-300 hover:text-[#FCD500] transition-colors">
                    <Phone className="w-4 h-4 text-[#E63027]" />
                    <span className="text-sm font-semibold">{loc.phone}</span>
                  </a>
                  <a href={`mailto:${loc.email}`} className="flex items-center gap-3 text-neutral-300 hover:text-[#FCD500] transition-colors">
                    <Mail className="w-4 h-4 text-[#E63027]" />
                    <span className="text-sm">{loc.email}</span>
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 card-sharp p-8 md:p-10"
          data-testid="hours-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-[#E63027]" />
            <h3 className="font-display text-3xl text-white tracking-wide">Horaires d'ouverture</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {HOURS.map((h) => (
              <div key={h.day} className={`p-4 border ${h.closed ? 'border-[#E63027]/40 bg-[#E63027]/5' : 'border-[#3A3A42] bg-[#1B1B1F]'}`}>
                <p className="text-xs tracking-widest uppercase font-bold text-[#FCD500] mb-2">{h.day}</p>
                <p className={`text-xs ${h.closed ? 'text-[#E63027] font-bold uppercase' : 'text-neutral-300'}`}>{h.h}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-32 bg-[#1B1B1F]" data-testid="faq-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="text-xs tracking-[0.4em] uppercase text-[#E63027] font-bold mb-3">— Questions fréquentes</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
              Vos<br />questions,<br /><span className="text-[#FCD500]">nos</span> réponses.
            </h2>
          </motion.div>
          <div className="lg:col-span-7 space-y-3">
            {FAQS.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="border border-[#3A3A42] bg-[#25252B] hover:border-[#E63027]/40 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-bold text-white text-base md:text-lg">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#E63027] flex-shrink-0 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${open === i ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-5 md:px-6 pb-5 md:pb-6 text-neutral-400 leading-relaxed">{f.a}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#141418] border-t border-white/10 pt-20 pb-10" data-testid="site-footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div>
            <Logo />
            <p className="mt-6 text-neutral-400 text-sm leading-relaxed">
              Spécialiste pièces auto et accessoires en Haute-Savoie. Marques OEM, devis gratuit, garantie pièces.
            </p>
          </div>
          <div>
            <h4 className="font-display text-xl text-white tracking-wider mb-4">Navigation</h4>
            <ul className="space-y-2 text-sm text-neutral-400">
              <li><a href="#categories" className="hover:text-[#E63027] transition-colors">Catégories</a></li>
              <li><a href="#pourquoi" className="hover:text-[#E63027] transition-colors">Pourquoi nous</a></li>
              <li><a href="#devis" className="hover:text-[#E63027] transition-colors">Demander un devis</a></li>
              <li><a href="#agences" className="hover:text-[#E63027] transition-colors">Nos agences</a></li>
              <li><a href="#faq" className="hover:text-[#E63027] transition-colors">FAQ</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-xl text-white tracking-wider mb-4">Ville-la-Grand</h4>
            <ul className="space-y-2 text-sm text-neutral-400">
              <li>16 Rue Deux Montagnes au Québec</li>
              <li>74100 Ville-la-Grand</li>
              <li><a href="tel:+33451420604" className="hover:text-[#FCD500] font-semibold">+33 4 51 42 06 04</a></li>
              <li><a href="mailto:abpa74100@gmail.com" className="hover:text-[#FCD500]">abpa74100@gmail.com</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-xl text-white tracking-wider mb-4">Marnaz</h4>
            <ul className="space-y-2 text-sm text-neutral-400">
              <li>597 avenue du Mont-Blanc</li>
              <li>74460 Marnaz</li>
              <li><a href="tel:+33451420360" className="hover:text-[#FCD500] font-semibold">+33 4 51 42 03 60</a></li>
              <li><a href="mailto:abap74460@gmail.com" className="hover:text-[#FCD500]">abap74460@gmail.com</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-neutral-500 tracking-wider uppercase">© {new Date().getFullYear()} AB Pièces Auto · Ville-la-Grand & Marnaz · Tous droits réservés</p>
          <div className="angle-divider w-32" />
        </div>
      </div>
    </footer>
  );
}

function FloatingWhatsApp() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <a
      href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Bonjour, je souhaite un devis pour une pièce auto.")}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`fixed bottom-6 right-6 z-40 group transition-all duration-500 ease-out ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-32 opacity-0 pointer-events-none'
      }`}
      data-testid="floating-whatsapp"
      aria-label="Contactez-nous sur WhatsApp"
    >
      <div className="relative pulse-ring">
        <div className="w-16 h-16 bg-[#25D366] hover:bg-[#1faf54] rounded-full flex items-center justify-center shadow-2xl transition-all hover:scale-110">
          <svg viewBox="0 0 32 32" className="w-9 h-9" fill="white" aria-hidden="true">
            <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.916 2.722.916.817 0 2.15-.516 2.495-1.318.187-.43.187-.802.115-1.246-.36-.244-.802-.488-1.232-.703-.302-.143-.602-.302-.86-.5-.072-.058-.14-.116-.244-.116m-3.025 8.27c-1.95 0-3.84-.516-5.485-1.535l-.387-.243-4.013 1.05 1.07-3.93-.258-.41a10.844 10.844 0 0 1-1.66-5.762c0-5.99 4.882-10.87 10.87-10.87 5.99 0 10.872 4.882 10.872 10.87 0 5.99-4.883 10.83-10.87 10.83m9.243-20.07a13.082 13.082 0 0 0-9.242-3.825C8.794 1.58 2.86 7.514 2.86 14.844c0 2.336.616 4.626 1.79 6.65L2.745 28.42l7.075-1.85a13.05 13.05 0 0 0 6.235 1.59h.014c7.276 0 13.21-5.92 13.21-13.27 0-3.522-1.39-6.836-3.882-9.328"/>
          </svg>
        </div>
      </div>
      <span className="hidden md:flex absolute right-20 top-1/2 -translate-y-1/2 bg-black border border-white/10 text-white text-xs uppercase tracking-widest font-bold px-3 py-2 whitespace-nowrap items-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        WhatsApp
      </span>
    </a>
  );
}

// ==================== APP ====================

function App() {
  return (
    <div className="App grain bg-[#1B1B1F] text-white">
      <Header />
      <main>
        <Hero />
        <BrandsMarquee />
        <Categories />
        <WhyUs />
        <QuoteForm />
        <Locations />
        <FAQ />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
